/**
 * Social Sharing Utilities
 * Centralized functions for generating social media sharing URLs
 * and managing social platform configurations
 */

/**
 * Social platform configurations with brand colors and icon mappings
 */
export const SOCIAL_PLATFORMS = {
  facebook: {
    name: 'Facebook',
    icon: 'uil uil-facebook',
    color: '#4267B2',
    order: 1
  },
  x: {
    name: 'X',
    icon: 'uil uil-twitter-alt',
    color: '#000000',
    order: 2
  },
  threads: {
    name: 'Threads',
    icon: 'uil uil-at',
    color: '#000000',
    order: 3
  },
  linkedin: {
    name: 'LinkedIn',
    icon: 'uil uil-linkedin',
    color: '#0A66C2',
    order: 4
  },
  whatsapp: {
    name: 'WhatsApp',
    icon: 'uil uil-whatsapp',
    color: '#25D366',
    order: 5
  },
  reddit: {
    name: 'Reddit',
    icon: 'uil uil-reddit-alien-alt',
    color: '#FF4500',
    order: 6
  },
  pinterest: {
    name: 'Pinterest',
    icon: 'uil uil-pinterest',
    color: '#E60023',
    order: 7
  }
}

/**
 * Generate sharing URL for a specific social platform
 * @param {string} platform - The social platform key (facebook, twitter, etc.)
 * @param {string} url - The URL to share
 * @returns {string} The platform-specific sharing URL
 */
export function getSocialShareUrl(platform, url) {
  const encodedUrl = encodeURIComponent(url)
  
  const urlTemplates = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    x: `https://twitter.com/intent/tweet?url=${encodedUrl}`,
    // Support legacy 'twitter' key for backwards compatibility
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}`,
    threads: `https://www.threads.net/intent/post?text=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodedUrl}`,
    reddit: `https://www.reddit.com/submit?url=${url}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}`
  }
  
  return urlTemplates[platform.toLowerCase()] || ''
}

/**
 * Get all enabled social platforms for a given configuration
 * @param {Object} config - Configuration object with platform enable flags
 * @returns {Array} Array of enabled platform objects
 */
export function getEnabledPlatforms(config) {
  if (!config) return []
  
  return Object.keys(SOCIAL_PLATFORMS)
    .filter(key => {
      const enabledKey = `${key.charAt(0).toUpperCase() + key.slice(1)}Enabled`
      return config[enabledKey] === true
    })
    .map(key => ({
      key,
      ...SOCIAL_PLATFORMS[key]
    }))
    .sort((a, b) => a.order - b.order)
}

/**
 * Get social platform configuration by key
 * @param {string} platform - The social platform key
 * @returns {Object} Platform configuration object
 */
export function getPlatformConfig(platform) {
  return SOCIAL_PLATFORMS[platform.toLowerCase()] || null
}

/**
 * Get all social platforms in order
 * @returns {Array} Array of all platform objects
 */
export function getAllPlatforms() {
  return Object.keys(SOCIAL_PLATFORMS)
    .map(key => ({
      key,
      ...SOCIAL_PLATFORMS[key]
    }))
    .sort((a, b) => a.order - b.order)
}

